import React, { useMemo } from 'react';
import { IntlShape, MessageDescriptor, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelHead } from '../../components/primitives/Panel';
import type { CampaignRow } from '../outreachTypes';
import {
  outreachAllResultsPath,
  outreachBuilderPath,
  outreachCampaignPath,
  outreachResultsPath,
} from '../outreachPaths';
import { kindsLine, monthDay } from '../live/campaignFormat';
import { isPitchKind } from '../live/pitchKindCopy';
import { homeMessages as messages } from './homeMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Home.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The page of a campaign: the setup for a draft, else the campaign page. */
export function campaignPathOf(row: Pick<CampaignRow, 'campaignUuid' | 'status' | 'catalogItemUuid'>): string {
  if (row.status === 'draft') {
    return outreachBuilderPath({ campaign: row.campaignUuid, song: row.catalogItemUuid });
  }
  return outreachCampaignPath(row.campaignUuid);
}

const RUNNING = ['scheduled', 'sending'];

/** The chip of a row (design outreach-v2 i1): its text and its colour. */
function chipOf(status: string): { label: MessageDescriptor; tone: 'pos' | 'vio' | 'muted' } {
  if (RUNNING.includes(status)) {
    return { label: messages.chipPitching, tone: 'pos' };
  }
  if (status === 'searching' || status === 'writing') {
    return { label: messages.chipLooking, tone: 'vio' };
  }
  if (status === 'review') {
    return { label: messages.chipReview, tone: 'vio' };
  }
  if (status === 'paused') {
    return { label: messages.chipPaused, tone: 'muted' };
  }
  if (status === 'draft') {
    return { label: messages.chipDraft, tone: 'muted' };
  }
  return { label: messages.chipDone, tone: 'muted' };
}

/** "Out Oct 28 · playlists, blogs · until Dec 23", or the end line of a done campaign. */
export function campaignRowLine(intl: IntlShape, row: CampaignRow): string {
  if (row.status === 'done') {
    return intl.formatMessage(messages.rowEnded, { sent: row.sent, added: row.placed });
  }
  if (row.status === 'draft') {
    return intl.formatMessage(messages.rowDraft);
  }
  const parts = [
    row.releaseDate === null
      ? intl.formatMessage(messages.rowOutNoDate)
      : intl.formatMessage(messages.rowOut, { date: monthDay(intl, row.releaseDate) }),
  ];
  const kinds = row.kinds.filter(isPitchKind);
  if (kinds.length > 0) {
    parts.push(kindsLine(intl, kinds).toLowerCase());
  }
  if (row.endsAt !== null) {
    parts.push(intl.formatMessage(messages.rowUntil, { date: monthDay(intl, row.endsAt) }));
  }
  return parts.join(' · ');
}

interface HomeCampaignsProps {
  campaigns: CampaignRow[];
  /** The pitches a day of the artist (`home.today.rate`): the head says how the songs share it. */
  rate: number;
}

/**
 * "Songs Plug is pitching" (design outreach-v2 i1): one row per campaign with its part of the daily
 * rate, its state, and Open (or Results for a done campaign).
 */
const HomeCampaignsComponent: React.FC<HomeCampaignsProps> = ({ campaigns, rate }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const running = useMemo(() => campaigns.filter((row) => RUNNING.includes(row.status)).length, [campaigns]);

  const rows = useMemo(
    () =>
      campaigns.map((row) => {
        const chip = chipOf(row.status);
        const isDone = row.status === 'done';
        const path = isDone ? outreachResultsPath(row.campaignUuid) : campaignPathOf(row);
        return (
          <div
            key={row.campaignUuid}
            className={clsx(styles.campaignRow, isDone && styles.campaignDone)}
            data-testid="outreach-campaign-row"
          >
            {row.coverUrl !== null ? (
              <img className={styles.campaignCover} src={row.coverUrl} alt="" />
            ) : (
              <span className={styles.campaignCover} />
            )}
            <span className={shared.cellTitle}>
              <b>{row.songTitle}</b>
              <small>{campaignRowLine(intl, row)}</small>
            </span>
            <span className={styles.campaignRate} data-testid="outreach-campaign-rate">
              {isDone ? (
                '—'
              ) : (
                <>
                  <b>{intl.formatNumber(row.dailyRate)}</b> {intl.formatMessage(messages.rowRate)}
                </>
              )}
            </span>
            <span
              className={clsx(
                shared.chip,
                chip.tone === 'pos' && shared.chipPos,
                chip.tone === 'vio' && shared.chipVio,
                chip.tone === 'muted' && shared.chipMuted,
                styles.hideNarrow
              )}
            >
              {intl.formatMessage(chip.label)}
            </span>
            <SMLButton
              size="sm"
              outline
              omitCap
              // eslint-disable-next-line no-void
              onClick={() => void navigate(path)}
            >
              {intl.formatMessage(isDone ? messages.results : messages.open)}
            </SMLButton>
          </div>
        );
      }),
    [campaigns, intl, navigate]
  );

  return (
    <Panel as="section">
      <PanelHead
        title={intl.formatMessage(messages.campaignsTitle)}
        right={
          <span className={shared.row}>
            <span className={shared.small}>
              {intl.formatMessage(running > 1 ? messages.campaignsShared : messages.campaignsRate, { rate })}
            </span>
            {/* Until the Pitch tabs of navigation v2 merge, the only way to the results of all songs. */}
            <SMLButton
              size="sm"
              color="link"
              omitCap
              // eslint-disable-next-line no-void
              onClick={() => void navigate(outreachAllResultsPath())}
              testID="home-all-results"
            >
              {intl.formatMessage(messages.allResults)}
            </SMLButton>
          </span>
        }
      />
      <div>{rows}</div>
    </Panel>
  );
};

export const HomeCampaigns = TypedMemo(HomeCampaignsComponent);
